import { resolveIcon } from '../../styles/icons'
import styled from 'styled-components'
import { Link, useNavigate } from 'react-router-dom'
import { AppIconColor } from '../../styles/IconStyle'
import usePath from '../../hooks/usePath'
import DisplayElement from '../display/DisplayElement'
import * as BsIcons from 'react-icons/bs'
import { TableDecorator } from './Table'
import { useState, useEffect } from 'react'
import { saveToLS, getFromLS } from '../../utils/localStore/localStore'
import {
    TableListMobile as TableList,
    TableListItemMobile as TableListItem,
    CardWrapper,
    Tags,
    Flex,
    LeftTag,
    LeftSubTag,
    RightTag,
    RightSubTag,
    IconWrapper,
    NoDataMobile as NoData,
    createSlugPath,
    hasValidData
} from './Common'

const TableLink = styled(Link)`
    text-decoration: none;
    flex: 1;
    display: flex;
`

const ExpandIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.primary || '#007bff'};
    font-size: 0.875rem;
    padding: 0.25rem;
    margin-right: 0.5rem;
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.table.hover || 'rgba(0,0,0,0.05)'};
        border-radius: 0.25rem;
    }
`

const NLevelItem = styled(TableListItem)`
    padding-left: ${({ $depth }) => `${($depth || 0) * 1.5 + 0.5}rem`};
    background-color: ${({ theme, $depth }) =>
        ($depth || 0) > 0 ? (theme.table?.childBackground || 'rgba(0,0,0,0.02)') : 'transparent'
    };
`

const TableRowsHierarchical_m = ({
    endpoint,
    result,
    mobile,
    slug,
    tableKey = 'id',
    path = '',
    hierarchicalConfig,
    filter,
    clickLine,
    onExpand
}) => {
    const { url } = usePath(path)
    const navigate = useNavigate()

    const [expandedParents, setExpandedParents] = useState({})

    const isNLevelMode = hierarchicalConfig && 'parentValue' in hierarchicalConfig

    useEffect(() => {
        const storageKey = `hierarchical_expanded_${endpoint}_${filter || 'default'}`
        const savedState = getFromLS(storageKey)
        if (savedState) setExpandedParents(savedState)
    }, [endpoint, filter])

    useEffect(() => {
        const storageKey = `hierarchical_expanded_${endpoint}_${filter || 'default'}`
        saveToLS(storageKey, null, expandedParents)
    }, [expandedParents, endpoint, filter])

    const slugPath = createSlugPath(slug)

    if (!hasValidData(result)) {
        return NoData
    }

    const allItems = result.data.pages.flatMap(page => page.data || [])

    const toggleParent = (parentId) => {
        setExpandedParents(prev => {
            const newState = { ...prev, [parentId]: !prev[parentId] }
            Object.keys(newState).forEach(key => {
                if (!newState[key]) delete newState[key]
            })
            return newState
        })
    }

    const handleNavigate = (e, item) => {
        e.preventDefault()
        e.stopPropagation()
        navigate(`${url}${item[tableKey]}${slugPath}`)
    }

    // ---- N-level mode ----
    if (isNLevelMode) {
        const { parentField, parentValue } = hierarchicalConfig

        const childrenByParent = {}
        allItems.forEach(item => {
            const pid = item[parentField]
            if (pid != null && pid !== '') {
                if (!childrenByParent[pid]) childrenByParent[pid] = []
                childrenByParent[pid].push(item)
            }
        })

        const rootItems = allItems.filter(item => item[parentField] == parentValue)

        const renderItem = (item, depth) => {
            const id = item[tableKey]
            const children = childrenByParent[id] || []
            const hasChildren = children.length > 0
            const isExpanded = !!expandedParents[id]

            return (
                <div key={id}>
                    <NLevelItem
                        $depth={depth}
                        data-testid={`${endpoint}.list.${id}.m`}
                        onClick={(e) => {
                            if (hasChildren) {
                                e.preventDefault()
                                toggleParent(id)
                            }
                        }}
                    >
                        <CardWrapper>
                            <Flex>
                                <IconWrapper
                                    $padding={mobile?.left?.iconPadding || '0'}
                                    $clickable={true}
                                    onClick={(e) => handleNavigate(e, item)}
                                >
                                    {mobile?.left?.icon ? resolveIcon(mobile?.left?.icon) : <></>}
                                </IconWrapper>
                                {hasChildren && (
                                    <ExpandIndicator onClick={(e) => { e.stopPropagation(); toggleParent(id) }}>
                                        {isExpanded ? <BsIcons.BsChevronDown /> : <BsIcons.BsChevronRight />}
                                    </ExpandIndicator>
                                )}
                                <Tags>
                                    <LeftTag>
                                        <DisplayElement
                                            string={item[mobile?.left?.tag.field]}
                                            type={mobile?.left?.tag.type}
                                            prefix={mobile?.left?.tag.prefix}
                                        />
                                        {mobile?.left?.tag.decorator ? <TableDecorator>{item[mobile?.left?.tag.decorator]}</TableDecorator> : <></>}
                                    </LeftTag>
                                    <LeftSubTag>
                                        <DisplayElement
                                            string={item[mobile?.left?.subTag.field]}
                                            type={mobile?.left?.subTag.type}
                                            prefix={mobile?.left?.subTag.prefix}
                                        />
                                        {mobile?.left?.subTag.decorator ? <TableDecorator>{item[mobile?.left?.subTag.decorator]}</TableDecorator> : <></>}
                                    </LeftSubTag>
                                </Tags>
                            </Flex>
                            <Flex>
                                <div>
                                    {mobile?.right?.tag ? (
                                        <RightTag>
                                            <DisplayElement
                                                string={item[mobile?.right?.tag.field]}
                                                type={mobile?.right?.tag.type}
                                                prefix={mobile?.right?.tag.prefix}
                                            />
                                            {mobile?.right?.tag.decorator ? <TableDecorator>{item[mobile?.right?.tag.decorator]}</TableDecorator> : <></>}
                                        </RightTag>
                                    ) : <></>}
                                    {mobile?.right?.subTag ? (
                                        <RightSubTag>
                                            <DisplayElement
                                                string={item[mobile?.right?.subTag.field]}
                                                type={mobile?.right?.subTag.type}
                                                prefix={mobile?.right?.subTag.prefix}
                                            />
                                            {mobile?.right?.subTag.decorator ? <TableDecorator>{item[mobile?.right?.subTag.decorator]}</TableDecorator> : <></>}
                                        </RightSubTag>
                                    ) : <></>}
                                </div>
                                {mobile?.right?.icon ? (
                                    <IconWrapper $clickable={true} onClick={(e) => handleNavigate(e, item)}>
                                        {resolveIcon(mobile?.right?.icon)}
                                    </IconWrapper>
                                ) : <></>}
                            </Flex>
                        </CardWrapper>
                    </NLevelItem>
                    {isExpanded && children.map(child => renderItem(child, depth + 1))}
                </div>
            )
        }

        return (
            <TableList>
                <AppIconColor>
                    {rootItems.map(item => renderItem(item, 0))}
                </AppIconColor>
            </TableList>
        )
    }

    // ---- Legacy 2-level mode ----
    const { field: childField, value: childValue, parentField } = hierarchicalConfig

    const parents = allItems.filter(item => item[childField] !== childValue)
    const children = allItems.filter(item => item[childField] === childValue)

    const childrenByParent = children.reduce((acc, child) => {
        const parentId = child[parentField]
        if (!acc[parentId]) acc[parentId] = []
        acc[parentId].push(child)
        return acc
    }, {})

    const renderLegacyItem = (item, isChild = false) => {
        const itemId = item[tableKey]
        const hasChildren = !isChild && childrenByParent[itemId]?.length > 0
        const isExpanded = expandedParents[itemId]

        return (
            <TableListItem
                key={itemId}
                $isChild={isChild}
                data-testid={`${endpoint}.list.${itemId}.m`}
                onClick={(e) => {
                    if (hasChildren) {
                        e.preventDefault()
                        e.stopPropagation()
                        toggleParent(itemId)
                    } else if (isChild) {
                        e.preventDefault()
                        e.stopPropagation()
                        navigate(`${url}${item[tableKey]}${slugPath}`)
                    }
                }}
            >
                <CardWrapper>
                    <Flex>
                        <IconWrapper
                            $padding={mobile?.left?.iconPadding || '0'}
                            $clickable={true}
                            onClick={(e) => handleNavigate(e, item)}
                        >
                            {mobile?.left?.icon ? resolveIcon(mobile?.left?.icon) : <></>}
                        </IconWrapper>
                        {hasChildren && (
                            <ExpandIndicator onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleParent(itemId) }}>
                                {isExpanded ? <BsIcons.BsChevronDown /> : <BsIcons.BsChevronRight />}
                            </ExpandIndicator>
                        )}
                        <Tags>
                            <LeftTag>
                                <DisplayElement
                                    string={item[mobile?.left?.tag.field]}
                                    type={mobile?.left?.tag.type}
                                    prefix={mobile?.left?.tag.prefix}
                                />
                                {mobile?.left?.tag.decorator ? <TableDecorator>{item[mobile?.left?.tag.decorator]}</TableDecorator> : <></>}
                            </LeftTag>
                            <LeftSubTag>
                                <DisplayElement
                                    string={item[mobile?.left?.subTag.field]}
                                    type={mobile?.left?.subTag.type}
                                    prefix={mobile?.left?.subTag.prefix}
                                />
                                {mobile?.left?.subTag.decorator ? <TableDecorator>{item[mobile?.left?.subTag.decorator]}</TableDecorator> : <></>}
                            </LeftSubTag>
                        </Tags>
                    </Flex>
                    <Flex>
                        <div>
                            {mobile?.right?.tag ? (
                                <RightTag>
                                    <DisplayElement
                                        string={item[mobile?.right?.tag.field]}
                                        type={mobile?.right?.tag.type}
                                        prefix={mobile?.right?.tag.prefix}
                                    />
                                    {mobile?.right?.tag.decorator ? <TableDecorator>{item[mobile?.right?.tag.decorator]}</TableDecorator> : <></>}
                                </RightTag>
                            ) : <></>}
                            {mobile?.right?.subTag ? (
                                <RightSubTag>
                                    <DisplayElement
                                        string={item[mobile?.right?.subTag.field]}
                                        type={mobile?.right?.subTag.type}
                                        prefix={mobile?.right?.subTag.prefix}
                                    />
                                    {mobile?.right?.subTag.decorator ? <TableDecorator>{item[mobile?.right?.subTag.decorator]}</TableDecorator> : <></>}
                                </RightSubTag>
                            ) : <></>}
                        </div>
                        {mobile?.right?.icon ? (
                            <IconWrapper $clickable={true} onClick={(e) => handleNavigate(e, item)}>
                                {resolveIcon(mobile?.right?.icon)}
                            </IconWrapper>
                        ) : <></>}
                    </Flex>
                </CardWrapper>
            </TableListItem>
        )
    }

    return (
        <TableList>
            <AppIconColor>
                {parents.map(parent => {
                    const parentId = parent[tableKey]
                    const isExpanded = expandedParents[parentId]
                    const parentChildren = childrenByParent[parentId] || []

                    return (
                        <div key={parentId}>
                            {renderLegacyItem(parent, false)}
                            {isExpanded && parentChildren.map(child => renderLegacyItem(child, true))}
                        </div>
                    )
                })}
            </AppIconColor>
        </TableList>
    )
}

export default TableRowsHierarchical_m
